import React, { useContext, useState } from 'react';
import axios from 'axios';
import { Pencil, X } from 'lucide-react';

import { UserContext } from '../../context/UserContext';
import { avisarSmackCambiado } from '../../lib/smackBus';
import CaptureAnnotator from './CaptureAnnotator';

// Mismo tope que MessageComposer y que el backend. La captura de una página
// larga a escala 2 lo pasa de sobra, y sin comprobarlo el envío moría con un
// error genérico y sin más salida que cancelar.
const MAX_ADJUNTO_BYTES = 10 * 1024 * 1024;

function dataUrlAFichero(dataUrl) {
  const [cabecera, datos] = dataUrl.split(',');
  const mime = cabecera.match(/data:(.*?);/)?.[1] || 'image/png';
  const binario = atob(datos);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i += 1) bytes[i] = binario.charCodeAt(i);
  return new File([bytes], 'pantallazo.png', { type: mime });
}

function contextoAuto(ruta) {
  const ua = navigator.userAgent || '';
  // UA recortado: familia de navegador/SO basta para el triage; el UA entero es ruido.
  const uaCorto = ua.length > 120 ? `${ua.slice(0, 120)}…` : ua;
  return `— Contexto: ${ruta} · ${window.innerWidth}×${window.innerHeight} · ${uaCorto}`;
}

/**
 * Overlay del reporte in situ: pantallazo YA capturado (con opción de quitarlo),
 * texto, y contexto automático (ruta, viewport, dispositivo) anexado al cuerpo
 * — el club no rellena nada que la app ya sepa. Publica en el canal "general"
 * del venue actual.
 */
export default function ReportComposer({ captura, ruta, onCerrar }) {
  const userCtx = useContext(UserContext);
  const [token, , , venueID] = userCtx;

  const [texto, setTexto] = useState('');
  // La captura es editable: el anotador (círculos/flechas en rojo) la sustituye
  // por la versión aplanada — el adjunto que viaja es el PNG anotado.
  const [capturaActual, setCapturaActual] = useState(captura || null);
  const [conCaptura, setConCaptura] = useState(Boolean(captura));
  const [anotando, setAnotando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [hecho, setHecho] = useState(false);
  const [error, setError] = useState('');

  const enviar = async () => {
    const body = texto.trim();
    if (!body || enviando) return;
    setEnviando(true);
    setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // El destino es el canal "general" del club actual.
      const canales = await axios.get('/v1/smack/channels', { headers });
      const lista = Array.isArray(canales.data) ? canales.data : [];
      const canal =
        lista.find((c) => String(c.venue_id) === String(venueID) && c.name === 'general') ||
        lista.find((c) => String(c.venue_id) === String(venueID));
      if (!canal) {
        setError('Este club no tiene canal de Smack todavía.');
        setEnviando(false);
        return;
      }
      const formData = new FormData();
      formData.append('body', `${body}\n\n${contextoAuto(ruta)}`);
      if (conCaptura && capturaActual) {
        const fichero = dataUrlAFichero(capturaActual);
        if (fichero.size > MAX_ADJUNTO_BYTES) {
          setError('El pantallazo pesa más de 10 MB. Envía el reporte sin captura.');
          setEnviando(false);
          return;
        }
        formData.append('file', fichero);
      }
      await axios.post(`/v1/smack/channels/${canal.id}/messages`, formData, { headers });
      avisarSmackCambiado();
      setHecho(true);
      setTimeout(onCerrar, 1400);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo enviar el reporte.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div
      data-smack-report="true"
      className="fixed inset-0 z-[100020] flex items-end justify-center bg-black/40 p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Reportar un problema"
    >
      {anotando && capturaActual ? (
        <CaptureAnnotator
          dataUrl={capturaActual}
          onGuardar={(nuevo) => {
            setCapturaActual(nuevo);
            setAnotando(false);
          }}
          onCancelar={() => setAnotando(false)}
        />
      ) : null}
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl">
        <div className="flex items-center justify-between pb-2">
          <h2 className="text-base font-semibold text-gray-900">Reportar un problema</h2>
          <button
            type="button"
            onClick={onCerrar}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {hecho ? (
          <p className="py-6 text-center text-sm font-medium text-green-700">
            Reporte enviado. ¡Gracias!
          </p>
        ) : (
          <>
            {conCaptura && capturaActual ? (
              <div className="relative mb-3">
                <img
                  src={capturaActual}
                  alt="Pantallazo capturado"
                  className="max-h-40 w-full rounded-lg border border-gray-200 object-contain"
                />
                <div className="absolute right-1.5 top-1.5 flex gap-1">
                  <button
                    type="button"
                    onClick={() => setAnotando(true)}
                    className="flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-[11px] font-medium text-white"
                    aria-label="Anotar pantallazo"
                  >
                    <Pencil size={12} /> Anotar
                  </button>
                  <button
                    type="button"
                    onClick={() => setConCaptura(false)}
                    className="rounded-full bg-black/60 p-1 text-white"
                    aria-label="Quitar pantallazo"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <p className="mb-3 text-xs text-gray-400">
                {captura ? 'Pantallazo descartado.' : 'No se pudo capturar la pantalla; el reporte va solo con texto.'}
              </p>
            )}

            <textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              rows={3}
              autoFocus
              placeholder="¿Qué ha pasado? Cuéntalo con tus palabras."
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#FF5400] focus:outline-none focus:ring-1 focus:ring-[rgba(255,84,0,0.15)]"
            />
            <p className="pt-1 text-[11px] text-gray-400">
              Se adjunta solo la pantalla actual y datos del dispositivo. Nada más.
            </p>
            {error ? <p className="pt-1 text-xs text-red-600">{error}</p> : null}

            <div className="flex justify-end gap-2 pt-3">
              <button
                type="button"
                onClick={onCerrar}
                className="rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-100"
              >
                Cancelar
              </button>
              {/* #FF5400: acento de marca de Smack (decisión 22-ago: se queda) — fuera de tokens.css a propósito: el design system es slate y este naranja es exclusivo del módulo. */}
              <button
                type="button"
                onClick={enviar}
                disabled={enviando || !texto.trim()}
                className="rounded-lg bg-[#FF5400] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {enviando ? 'Enviando…' : 'Enviar reporte'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
